/**
 * LiveKit Stream Viewer Component
 * Used for watching solo streams - subscribes to LiveKit room
 * Supports viewing both camera and screen share from broadcaster
 * FIXED: Audio subscription for microphone tracks
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  LiveKitRoom,
  VideoTrack,
  AudioTrack,
  useRemoteParticipants,
  useTracks,
  RoomAudioRenderer,
} from '@livekit/components-react';
import { Track, RoomEvent } from 'livekit-client';
import '@livekit/components-styles';
import { Video, Wifi, Monitor, Volume2, VolumeX } from 'lucide-react';

// Inner component that displays the streamer's video AND handles audio
const ViewerInner = ({ streamerName, onStreamerConnected }) => {
  const remoteParticipants = useRemoteParticipants();
  const [streamerConnected, setStreamerConnected] = useState(false);
  const [isViewingScreenShare, setIsViewingScreenShare] = useState(false);
  const [hasAudio, setHasAudio] = useState(false);
  
  // Subscribe to ALL track types: Camera, ScreenShare, Microphone, ScreenShareAudio
  const tracks = useTracks([
    { source: Track.Source.Camera, withPlaceholder: false },
    { source: Track.Source.ScreenShare, withPlaceholder: false },
    { source: Track.Source.Microphone, withPlaceholder: false },
    { source: Track.Source.ScreenShareAudio, withPlaceholder: false },
  ]);
  
  // Find video tracks
  const cameraTrack = tracks.find(t => 
    t.participant?.identity && 
    t.source === Track.Source.Camera &&
    t.publication?.track
  );
  
  const screenShareTrack = tracks.find(t => 
    t.participant?.identity && 
    t.source === Track.Source.ScreenShare &&
    t.publication?.track
  );
  
  // Find audio tracks
  const microphoneTrack = tracks.find(t => 
    t.participant?.identity && 
    t.source === Track.Source.Microphone &&
    t.publication?.track
  );
  
  const screenShareAudioTrack = tracks.find(t => 
    t.participant?.identity && 
    t.source === Track.Source.ScreenShareAudio &&
    t.publication?.track
  );
  
  // Prefer screen share when available, otherwise show camera
  const activeVideoTrack = screenShareTrack || cameraTrack;
  
  // Use screen share audio if screen sharing, otherwise mic
  const activeAudioTrack = screenShareAudioTrack || microphoneTrack;
  
  useEffect(() => {
    setIsViewingScreenShare(!!screenShareTrack);
  }, [screenShareTrack]);
  
  useEffect(() => {
    setHasAudio(!!activeAudioTrack);
    console.log('🔊 Audio track status:', {
      hasMic: !!microphoneTrack,
      hasScreenAudio: !!screenShareAudioTrack,
      activeAudio: !!activeAudioTrack
    });
  }, [microphoneTrack, screenShareAudioTrack, activeAudioTrack]);
  
  useEffect(() => {
    const hasStreamer = remoteParticipants.length > 0 && activeVideoTrack;
    setStreamerConnected(hasStreamer);
    
    console.log('📺 Viewer connection status:', {
      remoteParticipants: remoteParticipants.length,
      hasVideo: !!activeVideoTrack,
      hasAudio: !!activeAudioTrack,
      isScreenShare: !!screenShareTrack
    });
    
    if (hasStreamer && onStreamerConnected) {
      onStreamerConnected({
        identity: activeVideoTrack.participant.identity,
        name: activeVideoTrack.participant.name,
        isScreenSharing: !!screenShareTrack,
        hasAudio: !!activeAudioTrack
      });
    }
  }, [remoteParticipants, activeVideoTrack, activeAudioTrack, screenShareTrack, onStreamerConnected]);

  if (!streamerConnected) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="text-center p-8">
          <div className="relative mb-6">
            <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center animate-pulse">
              <Video className="h-12 w-12 text-white" />
            </div>
            <div className="absolute -top-1 -right-1 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center animate-bounce">
              <Wifi className="w-3 h-3 text-white" />
            </div>
          </div>
          <h3 className="text-xl font-bold text-white mb-2">
            Connecting to Stream...
          </h3>
          <p className="text-gray-400 mb-4">
            {streamerName || 'Streamer'} is live
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-500"></div>
            <span>Waiting for video...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-black relative">
      {/* Status indicators */}
      <div className="absolute top-3 left-3 z-20 flex items-center gap-2">
        {/* Screen share indicator */}
        {isViewingScreenShare && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-600/90 backdrop-blur-sm rounded-full">
            <Monitor className="w-4 h-4 text-white" />
            <span className="text-white text-sm font-medium">Screen Share</span>
          </div>
        )}
        
        {/* Audio indicator */}
        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full backdrop-blur-sm ${hasAudio ? 'bg-green-600/80' : 'bg-red-600/80'}`}>
          {hasAudio ? (
            <Volume2 className="w-3.5 h-3.5 text-white" />
          ) : (
            <VolumeX className="w-3.5 h-3.5 text-white" />
          )}
          <span className="text-white text-xs font-medium">
            {hasAudio ? 'Audio' : 'No Audio'}
          </span>
        </div>
      </div>
      
      {/* Video Track */}
      {activeVideoTrack && (
        <VideoTrack 
          trackRef={activeVideoTrack}
          className="w-full h-full"
          style={{ 
            objectFit: 'contain',
            transform: isViewingScreenShare ? 'none' : undefined 
          }}
        />
      )}
      
      {/* Audio Tracks - Render audio elements for playback */}
      {microphoneTrack && (
        <AudioTrack trackRef={microphoneTrack} />
      )}
      {screenShareAudioTrack && (
        <AudioTrack trackRef={screenShareAudioTrack} />
      )}
    </div>
  );
};

const LiveKitStreamViewer = ({ 
  token, 
  serverUrl, 
  roomName, 
  streamerName,
  onStreamerConnected,
  onError,
  onDisconnected 
}) => {
  const handleError = useCallback((error) => {
    console.error('LiveKit viewer error:', error);
    if (onError) {
      onError(error);
    }
  }, [onError]);

  const handleDisconnected = useCallback(() => {
    console.log('👋 LiveKit viewer disconnected from room');
    if (onDisconnected) {
      onDisconnected();
    }
  }, [onDisconnected]);

  const handleConnected = useCallback(() => {
    console.log('✅ LiveKit viewer connected to room:', roomName);
  }, [roomName]);

  if (!token || !serverUrl) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-900">
        <div className="text-center text-white">
          <Video className="h-16 w-16 mx-auto mb-4 opacity-50" />
          <p className="text-xl mb-2">Stream Unavailable</p>
          <p className="text-sm text-gray-400">Unable to connect to this stream</p>
        </div>
      </div>
    );
  }

  return (
    <LiveKitRoom
      token={token}
      serverUrl={serverUrl}
      connect={true}
      video={false}
      audio={false}
      onError={handleError}
      onDisconnected={handleDisconnected}
      onConnected={handleConnected}
      options={{
        // Enable audio playback
        audioCaptureDefaults: {
          autoGainControl: true,
          echoCancellation: true,
          noiseSuppression: true,
        },
        // Auto-subscribe to all tracks
        adaptiveStream: true,
        dynacast: true,
      }}
      style={{ height: '100%', width: '100%' }}
    >
      <ViewerInner 
        streamerName={streamerName}
        onStreamerConnected={onStreamerConnected} 
      />
      {/* RoomAudioRenderer handles audio playback for all subscribed audio tracks */}
      <RoomAudioRenderer />
    </LiveKitRoom>
  );
};

export default LiveKitStreamViewer;
